import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { useAuth } from '../../../context/AuthContext';
import { useTeam } from '../../../context/TeamContext';

const LeaderboardDisplay = ({ userId, teamId }) => {
  const { currentUser } = useAuth();
  const { currentTeam } = useTeam();
  const [leaderboardType, setLeaderboardType] = useState('personal'); // 'personal', 'team', 'global'
  const [period, setPeriod] = useState('weekly'); // 'weekly', 'monthly', 'allTime'
  const [personalLeaderboard, setPersonalLeaderboard] = useState([]);
  const [teamLeaderboard, setTeamLeaderboard] = useState([]);
  const [globalLeaderboard, setGlobalLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRank, setUserRank] = useState(null);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    loadLeaderboard();
  }, [userId, leaderboardType, period]);

  const loadLeaderboard = async () => {
    try {
      setLoading(true);

      if (leaderboardType === 'personal') {
        await loadPersonalLeaderboard();
      } else if (leaderboardType === 'team' && teamId) {
        await loadTeamLeaderboard();
      } else if (leaderboardType === 'global') {
        await loadGlobalLeaderboard();
      }
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPersonalLeaderboard = async () => {
    try {
      const usersRef = collection(db, 'users');
      let q;

      if (period === 'allTime') {
        q = query(usersRef, orderBy('totalPoints', 'desc'), limit(100));
      } else {
        // For weekly/monthly, we'll use totalPoints for now
        // In production, you'd want to calculate period-specific points
        q = query(usersRef, orderBy('totalPoints', 'desc'), limit(100));
      }

      const snapshot = await getDocs(q);
      const leaderboard = snapshot.docs.map((doc, index) => ({
        id: doc.id,
        rank: index + 1,
        ...doc.data()
      }));

      setPersonalLeaderboard(leaderboard);

      // Find user rank
      const userIndex = leaderboard.findIndex(u => u.id === userId);
      if (userIndex !== -1) {
        setUserRank(leaderboard[userIndex]);
      }
    } catch (error) {
      console.error('Error loading personal leaderboard:', error);
    }
  };

  const loadTeamLeaderboard = async () => {
    try {
      // Get all team members
      const teamRef = doc(db, 'teams', teamId);
      const teamSnap = await getDoc(teamRef);

      if (!teamSnap.exists()) {
        setTeamLeaderboard([]);
        return;
      }

      const teamData = teamSnap.data();
      const memberIds = teamData.members?.map(m => m.uid) || [];

      // Get member stats
      const members = await Promise.all(
        memberIds.map(async (memberId) => {
          const userRef = doc(db, 'users', memberId);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            return {
              id: memberId,
              ...userSnap.data()
            };
          }
          return null;
        })
      );

      // Filter nulls and sort by points
      const sortedMembers = members
        .filter(m => m !== null)
        .sort((a, b) => (b.totalPoints || 0) - (a.totalPoints || 0))
        .map((member, index) => ({
          ...member,
          rank: index + 1
        }));

      setTeamLeaderboard(sortedMembers);

      // Find user rank
      const userIndex = sortedMembers.findIndex(m => m.id === userId);
      if (userIndex !== -1) {
        setUserRank(sortedMembers[userIndex]);
      }
    } catch (error) {
      console.error('Error loading team leaderboard:', error);
    }
  };

  const loadGlobalLeaderboard = async () => {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, orderBy('totalPoints', 'desc'), limit(100));

      const snapshot = await getDocs(q);
      const leaderboard = snapshot.docs.map((doc, index) => ({
        id: doc.id,
        rank: index + 1,
        ...doc.data()
      }));

      setGlobalLeaderboard(leaderboard);

      // Find user rank
      const userIndex = leaderboard.findIndex(u => u.id === userId);
      if (userIndex !== -1) {
        setUserRank(leaderboard[userIndex]);
      }
    } catch (error) {
      console.error('Error loading global leaderboard:', error);
    }
  };

  const getCurrentLeaderboard = () => {
    if (leaderboardType === 'personal') return personalLeaderboard;
    if (leaderboardType === 'team') return teamLeaderboard;
    return globalLeaderboard;
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const renderLeaderboardRow = (entry, index) => {
    const isCurrentUser = entry.id === userId;
    const level = entry.level || 1;
    const mascot = entry.mascot || { nameThai: 'มือใหม่' };

    return (
      <div
        key={entry.id}
        className={`flex items-center justify-between p-4 rounded-lg ${
          isCurrentUser
            ? 'bg-blue-50 border-2 border-blue-500'
            : 'bg-gray-50 border border-gray-200'
        }`}
      >
        <div className="flex items-center space-x-4 flex-1">
          <div className="text-2xl font-bold w-12 text-center">
            {getRankIcon(entry.rank)}
          </div>
          <div className="flex-1">
            <div className="font-semibold text-gray-800">
              {entry.displayName || entry.email || 'ผู้ใช้'}
              {isCurrentUser && <span className="ml-2 text-blue-600">(คุณ)</span>}
            </div>
            <div className="text-sm text-gray-600">
              Level {level}: {mascot.nameThai}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-bold text-blue-600">{entry.totalPoints?.toLocaleString() || 0}</div>
          <div className="text-sm text-gray-600">Points</div>
        </div>
        <div className="text-right ml-4">
          <div className="font-bold text-green-600">{entry.currentXP?.toLocaleString() || 0}</div>
          <div className="text-sm text-gray-600">XP</div>
        </div>
        <div className="text-right ml-4">
          <div className="font-bold text-purple-600">{entry.badges?.length || 0}</div>
          <div className="text-sm text-gray-600">Badges</div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  const currentLeaderboard = getCurrentLeaderboard();

  return (
    <div className="space-y-6">
      {/* Type Selector */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex gap-2 mb-4 border-b border-gray-200">
          <button
            onClick={() => setLeaderboardType('personal')}
            className={`px-4 py-2 font-medium ${
              leaderboardType === 'personal'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Personal
          </button>
          {teamId && (
            <button
              onClick={() => setLeaderboardType('team')}
              className={`px-4 py-2 font-medium ${
                leaderboardType === 'team'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Team
            </button>
          )}
          <button
            onClick={() => setLeaderboardType('global')}
            className={`px-4 py-2 font-medium ${
              leaderboardType === 'global'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Global
          </button>
        </div>

        {/* Period Selector */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setPeriod('weekly')}
            className={`px-4 py-2 rounded-lg font-medium ${
              period === 'weekly'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Weekly
          </button>
          <button
            onClick={() => setPeriod('monthly')}
            className={`px-4 py-2 rounded-lg font-medium ${
              period === 'monthly'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setPeriod('allTime')}
            className={`px-4 py-2 rounded-lg font-medium ${
              period === 'allTime'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All Time
          </button>
        </div>
      </div>

      {/* User Rank Card */}
      {userRank && (
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg shadow-md p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm opacity-90 mb-1">อันดับของคุณ</div>
              <div className="text-3xl font-bold">{getRankIcon(userRank.rank)}</div>
              <div className="text-lg mt-2">{userRank.rank}</div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold">{userRank.totalPoints?.toLocaleString() || 0}</div>
              <div className="text-sm opacity-90">Points</div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold">{userRank.currentXP?.toLocaleString() || 0}</div>
              <div className="text-sm opacity-90">XP</div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold">{userRank.badges?.length || 0}</div>
              <div className="text-sm opacity-90">Badges</div>
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard List */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4">
          {leaderboardType === 'personal' && '📊 Personal Leaderboard'}
          {leaderboardType === 'team' && '👥 Team Leaderboard'}
          {leaderboardType === 'global' && '🌍 Global Leaderboard'}
        </h3>
        {currentLeaderboard.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-2">📊</div>
            <p>ยังไม่มีข้อมูล Leaderboard</p>
          </div>
        ) : (
          <div className="space-y-2">
            {currentLeaderboard.slice(0, 50).map((entry, index) => renderLeaderboardRow(entry, index))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaderboardDisplay;



